import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";
import { create } from "zustand";

import { dashboardKeys } from "Dashboard/API/keys";
import { navSectionKeys, useActiveSectionKey } from "NavBar/navBarStore";

import * as actions from "./actions";

const useWhatsNextStore = create((set, get) => ({
  activeAction: undefined,
  abortController: null,
  expectedPaths: [],
  expectedSectionKey: null,
  navButtonClicked: false,
  hasNavigatedAway: false,
  drawingActions: new Set(),

  setActiveAction: (newActiveAction, expectedPaths = []) => {
    const currentAction = get().activeAction;

    // If re-registering same action with paths, just update paths without aborting
    if (currentAction === newActiveAction && expectedPaths.length > 0) {
      set({ expectedPaths: ["/action-center", "/dashboard", ...expectedPaths] });
      return;
    }

    // Otherwise abort previous and create new
    get().abortController?.abort();

    set({
      activeAction: newActiveAction,
      abortController: new AbortController(),
      expectedPaths: ["/action-center", "/dashboard", ...expectedPaths],
      expectedSectionKey: null,
      navButtonClicked: false,
      hasNavigatedAway: false,
    });
  },

  setExpectedSectionKey: (sectionKey) => {
    set({ expectedSectionKey: sectionKey });
  },

  setNavButtonClicked: (value) => {
    set({ navButtonClicked: value });
  },

  setHasNavigatedAway: (value) => {
    set({ hasNavigatedAway: value });
  },

  clearActiveAction: () => {
    get().abortController?.abort();
    set({
      activeAction: undefined,
      abortController: null,
      expectedPaths: [],
      expectedSectionKey: null,
      navButtonClicked: false,
      hasNavigatedAway: false,
    });
  },

  registerDrawingAction: (action) => {
    const next = new Set(get().drawingActions);
    next.add(action);
    set({ drawingActions: next });
  },

  unregisterDrawingAction: (action) => {
    const next = new Set(get().drawingActions);
    next.delete(action);
    set({ drawingActions: next });
  },
}));

const useActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  const setActiveAction = useWhatsNextStore((s) => s.setActiveAction);

  return [activeAction, setActiveAction];
};

const useNetworkImageActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.networkImage, [activeAction]);
};

const useProfileImageActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.profileImage, [activeAction]);
};

const useSendEmailActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.sendEmail, [activeAction]);
};

const useSendTextActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.sendText, [activeAction]);
};

const useAddAdminActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.addAdmin, [activeAction]);
};

const useCreateSignupActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.createSignup, [activeAction]);
};

const useTtjKeywordActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.ttjKeyword, [activeAction]);
};

const useCreateGroupActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.createGroup, [activeAction]);
};

const useAddMemberActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.addMember, [activeAction]);
};

const useImportMemberActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.importMember, [activeAction]);
};

const useCustomizeHomepageActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(
    () => activeAction === actions.customizeHomepage,
    [activeAction]
  );
};

const useAddFundActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.addFund, [activeAction]);
};

const useAddGiftsActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.addGifts, [activeAction]);
};

const useCreateCampaignActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.createCampaign, [activeAction]);
};

const useCreateProfileFieldActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(
    () => activeAction === actions.createProfileField,
    [activeAction]
  );
};

const useSetupDatabaseActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.setupDatabase, [activeAction]);
};

const useEnableContactUsActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(
    () => activeAction === actions.enableContactUs,
    [activeAction]
  );
};

const useSendWelcomeEmailActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(
    () => activeAction === actions.sendWelcomeEmail,
    [activeAction]
  );
};

const useCreateReportActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.createReport, [activeAction]);
};

const useRunDonorSummariesActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(
    () => activeAction === actions.runDonorSummaries,
    [activeAction]
  );
};

const useStartRESignupActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.startRESignup, [activeAction]);
};

const useStartRESetupActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.startRESetup, [activeAction]);
};

const useReviewPrayerRequestsActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(
    () => activeAction === actions.reviewPrayerRequests,
    [activeAction]
  );
};

const useReviewDuplicatesActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(
    () => activeAction === actions.reviewDuplicates,
    [activeAction]
  );
};

const useMissingEinNoticeActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.missingEinNotice, [activeAction]);
};

const useMissingEinAlertActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.missingEinAlert, [activeAction]);
};

const useMissingEinCriticalActiveAction = () => {
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  return useMemo(() => activeAction === actions.missingEinCritical, [activeAction]);
};

const useClearActiveAction = () => {
  return useWhatsNextStore((s) => s.clearActiveAction);
};

const useAbortSignal = () => {
  return useWhatsNextStore((s) => s.abortController?.signal);
};

const useWhatsNextRouteCleanup = () => {
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  const expectedPaths = useWhatsNextStore((s) => s.expectedPaths);
  const hasNavigatedAway = useWhatsNextStore((s) => s.hasNavigatedAway);
  const setHasNavigatedAway = useWhatsNextStore((s) => s.setHasNavigatedAway);
  const clearActiveAction = useWhatsNextStore((s) => s.clearActiveAction);

  useEffect(() => {
    if (!activeAction || expectedPaths.length === 0) return;

    // Normalize pathname - treat "/" as "/action-center" for WhatsNext flows
    const normalizedPathname = pathname === "/" ? "/action-center" : pathname;
    const isOnActionCenter = normalizedPathname === "/action-center" || normalizedPathname === "/dashboard";

    // Track if we've navigated away from action center
    if (!isOnActionCenter) {
      setHasNavigatedAway(true);
    }

    // If we're back on action center after navigating away, clear the flow
    if (isOnActionCenter && hasNavigatedAway) {
      clearActiveAction();
      queryClient.invalidateQueries({ queryKey: dashboardKeys.nextActions() });
      return;
    }

    // Original: clear if not on expected path
    const isOnExpectedPath = expectedPaths.some((path) =>
      normalizedPathname.startsWith(path)
    );

    if (!isOnExpectedPath) {
      clearActiveAction();
    }
  }, [pathname, activeAction, expectedPaths, hasNavigatedAway, setHasNavigatedAway, clearActiveAction, queryClient]);
};

const useExpectedSectionKey = () => {
  const expectedSectionKey = useWhatsNextStore((s) => s.expectedSectionKey);
  const setExpectedSectionKey = useWhatsNextStore((s) => s.setExpectedSectionKey);

  return [expectedSectionKey, setExpectedSectionKey];
};

/**
 * Clears the active action if the user opens a different nav section than expected.
 * This prevents the arrow flow from continuing when the user takes the wrong action.
 */
const useActiveSectionCleanup = () => {
  const [activeSectionKey] = useActiveSectionKey();
  const activeAction = useWhatsNextStore((s) => s.activeAction);
  const expectedSectionKey = useWhatsNextStore((s) => s.expectedSectionKey);
  const clearActiveAction = useWhatsNextStore((s) => s.clearActiveAction);

  // Track the previous section key to detect changes
  const prevSectionKeyRef = useRef(activeSectionKey);

  useEffect(() => {
    // Only check if there's an active action with an expected section
    if (!activeAction || !expectedSectionKey) {
      prevSectionKeyRef.current = activeSectionKey;
      return;
    }

    // Detect if section key actually changed
    const sectionChanged = activeSectionKey !== prevSectionKeyRef.current;
    prevSectionKeyRef.current = activeSectionKey;

    if (!sectionChanged) return;

    // If user opened a different section than expected, abort the flow
    // Ignore 'nothing' since that just means sections collapsed
    if (
      activeSectionKey !== navSectionKeys.nothing &&
      activeSectionKey !== expectedSectionKey
    ) {
      clearActiveAction();
    }
  }, [activeSectionKey, activeAction, expectedSectionKey, clearActiveAction]);
};

const useNavButtonClicked = () => {
  const navButtonClicked = useWhatsNextStore((s) => s.navButtonClicked);
  const setNavButtonClicked = useWhatsNextStore((s) => s.setNavButtonClicked);

  return [navButtonClicked, setNavButtonClicked];
};

const useDrawingActions = () => {
  return useWhatsNextStore((s) => s.drawingActions);
};

export {
  useActiveAction,
  useDrawingActions,
  useNetworkImageActiveAction,
  useProfileImageActiveAction,
  useSendEmailActiveAction,
  useSendTextActiveAction,
  useAddAdminActiveAction,
  useCreateSignupActiveAction,
  useTtjKeywordActiveAction,
  useCreateGroupActiveAction,
  useAddMemberActiveAction,
  useImportMemberActiveAction,
  useCustomizeHomepageActiveAction,
  useAddFundActiveAction,
  useAddGiftsActiveAction,
  useCreateCampaignActiveAction,
  useCreateProfileFieldActiveAction,
  useSetupDatabaseActiveAction,
  useEnableContactUsActiveAction,
  useSendWelcomeEmailActiveAction,
  useCreateReportActiveAction,
  useRunDonorSummariesActiveAction,
  useStartRESignupActiveAction,
  useStartRESetupActiveAction,
  useReviewPrayerRequestsActiveAction,
  useReviewDuplicatesActiveAction,
  useMissingEinNoticeActiveAction,
  useMissingEinAlertActiveAction,
  useMissingEinCriticalActiveAction,
  useClearActiveAction,
  useAbortSignal,
  useWhatsNextRouteCleanup,
  useExpectedSectionKey,
  useActiveSectionCleanup,
  useNavButtonClicked,
};
export default useWhatsNextStore;
